import React from 'react';
import { Button, Checkbox, Loader, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import XLSX from 'xlsx';
import { AccountInfoType, FormServFieldType } from '../../../../shared/types';
import { CustomFileType, ImportContactsFromType } from './types';
import { useUploadCsv } from '../../../../shared/hooks';
import {
  Center,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
} from '../../../../shared/styles';
import { useGetContactLists } from '../../../screens/lists/hooks';
import { getJsonFromContacts, schema } from './utils';
import ContactsImporter from './ContactsImporter';

type ImportContactsProps = {
  toggleDrawer: () => void;
  fullHeight?: boolean;
  executeSegment?: () => void;
  fields?: FormServFieldType[];
  accountInfo?: AccountInfoType;
  contactsCount: number;
};

const ImportContacts = (props: ImportContactsProps) => {
  const { t } = useTranslation();
  const {
    toggleDrawer,
    fullHeight = false,
    fields = [],
    executeSegment,
    accountInfo,
    contactsCount,
  } = props;
  const [, setToast] = useToasts();
  const [val, setVal] = React.useState(false);
  const {
    data = {},
    isLoading: isLoadingContactLists,
    isError: isContactListsError,
  } = useGetContactLists();
  const { contactList: contactLists = [] } = data || {};
  const { mutate: uploadCsv, isLoading: isUpploadingCsv } = useUploadCsv({
    callback: (success: boolean) => {
      if (success) {
        setToast({
          type: 'info',
          delay: 10000,
          showProgress: true,
          fixed: true,
          text: `${t('forms.toast.success.import_initiated')}`,
        });
        if (executeSegment) executeSegment();
        toggleDrawer();
      } else {
        setToast({
          type: 'error',
          delay: 3000,
          text: `${t('forms.toast.error.upload_failed')}`,
        });
      }
    },
  });

  const form = useForm<ImportContactsFromType>({
    resolver: yupResolver(schema(fields)),
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = form;

  const onSubmit = async (data: ImportContactsFromType) => {
    const { fileMetaData, importContacts, addToLists = [] } = data;
    const jsonData = getJsonFromContacts(
      fields,
      addToLists,
      fileMetaData.data || [],
      importContacts,
    );
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    // const wb: XLSX.WorkBook = XLSX.utils.book_new();
    // XLSX.utils.book_append_sheet(wb, ws);
    // XLSX.writeFile(wb, 'test.csv');
    const file = new File([csv], fileMetaData.name);
    uploadCsv(file);
  };
  const fileMetaData: CustomFileType = form.watch('fileMetaData') || {};

  if (isLoadingContactLists || isUpploadingCsv)
    return (
      <Center>
        <Loader />
        {isUpploadingCsv && (
          <>
            <div className="text">{t('uploading')}</div>
            <div className="sub-text">{t('contacts.upload_file')}</div>
          </>
        )}
      </Center>
    );

  if (isContactListsError)
    return <Center>{t('empty_state.message.network_error')}</Center>;

  return (
    <FormContainer
      onSubmit={handleSubmit(onSubmit, (err) => console.log('ERR', err))}
      height={fullHeight ? '100%' : '400px'}
    >
      <FormHeader hideBorder>
        <div className="heading">{t('contacts.import_contact.heading')}</div>
      </FormHeader>
      <FormContent>
        <FormField>
          <ContactsImporter
            component="import"
            contactLists={contactLists || []}
            isLoadingContactLists={isLoadingContactLists}
            fields={fields}
            form={form}
            accountInfo={accountInfo}
            contactsCount={contactsCount}
          />
        </FormField>
      </FormContent>
      {fileMetaData.name ? (
        <div className="disclaimer">
          <Checkbox
            checked={val}
            isRequired={true}
            name="disclaimer"
            onChange={() => {
              setVal(!val);
            }}
          >
            <span className="disclaimer-text">
              {t('contacts.import_contact.disclaimer_text')}
            </span>
          </Checkbox>
        </div>
      ) : null}
      <FormActions>
        <div />
        <div>
          <Button
            inline
            htmlType="reset"
            type="secondary"
            onClick={toggleDrawer}
          >
            {t('cancel')}
          </Button>
          <Button inline htmlType="submit" disabled={!isDirty || !val}>
            {t('save')}
          </Button>
        </div>
      </FormActions>
    </FormContainer>
  );
};

export default ImportContacts;
